import { memo, ReactNode } from 'react';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import {
  SINGLE_COLUMN_MAX_WIDTH,
  TWO_COLUMN_MAX_WIDTH,
  DEFAULT_SIDEBAR_WIDTH,
  DEFAULT_MARGIN,
  DEFAULT_MARGIN_MOBILE,
  DEFAULT_CARD_SPACER,
} from '@/constants/layout';

type TwoColumnLayoutProps = {
  /**
   * Optional header that spans the full width above the main/sidebar content.
   * Automatically aligns with the content below for consistent max-width.
   */
  header?: ReactNode;
  /** Main content area (left column on desktop). Can be a single component or array of components. */
  main: ReactNode;
  /**
   * Sidebar content (right column on desktop, optional).
   * Can be a single component, array of components, or undefined.
   * Automatically switches to single-column layout if sidebar is not provided.
   *
   * Note: Parent component should control visibility (e.g., pass undefined when not needed).
   * Don't rely on components returning null - they should be conditionally passed.
   */
  sidebar?: ReactNode;
  /**
   * Optional footer for action buttons (mobile/tablet only - hidden on desktop).
   * Automatically sticks to the bottom of the viewport on mobile and tablet.
   */
  footer?: ReactNode;
};

/**
 * A responsive two-column layout component with a fixed-width sidebar.
 *
 * Features:
 * - Optional header that auto-aligns with content below
 * - Optional footer that sticks to bottom on mobile/tablet (hidden on desktop)
 * - Fixed-width sidebar (DEFAULT_SIDEBAR_WIDTH = 384px)
 * - Flexible main content area
 * - Uses standard layout breakpoints (SINGLE_COLUMN_MAX_WIDTH, TWO_COLUMN_MAX_WIDTH)
 * - Automatically switches to single column if sidebar is undefined
 * - Mobile/tablet-responsive (stacks vertically on phone and tablet sizes)
 * - Desktop-only two-column layout (requires sufficient screen width)
 * - Centered container with consistent max widths
 *
 * Best Practice:
 * - Wrap multiple items in VStack in the parent for vertical stacking
 * - Parent controls sidebar visibility by conditionally passing component or undefined
 * - Use header prop for page titles/actions to ensure proper alignment
 * - Use footer prop for action buttons that should stick to bottom on mobile
 *
 * @example With header and sticky footer (mobile/tablet)
 * ```tsx
 * <TwoColumnLayout
 *   header={<PageHeader />}
 *   main={
 *     <VStack gap={2}>
 *       <Summary />
 *       <Positions />
 *     </VStack>
 *   }
 *   sidebar={<RewardsCard />}
 *   footer={<ActionButtons />}  // Sticks to bottom on mobile/tablet
 * />
 * ```
 *
 * @example Without sidebar (single column)
 * ```tsx
 * <TwoColumnLayout
 *   header={<PageHeader />}
 *   main={<MainContent />}
 * />
 * ```
 *
 * @example Conditional sidebar
 * ```tsx
 * const showSidebar = someCondition;
 *
 * <TwoColumnLayout
 *   header={<PageHeader />}
 *   main={<VStack gap={2}>...</VStack>}
 *   sidebar={showSidebar ? <SidebarCard /> : undefined}
 * />
 * ```
 */
export const TwoColumnLayout = memo(function TwoColumnLayout({
  header,
  main,
  sidebar,
  footer,
}: TwoColumnLayoutProps) {
  const { isPhone, isPhoneLandscape, isTablet } = useBreakpoints();
  const isMobile = isPhone || isPhoneLandscape || isTablet;
  const hasSidebar = sidebar != null;

  // Mobile/Tablet layout: single column stack with sticky footer
  if (isMobile) {
    return (
      <VStack width="100%" minHeight="100vh" justifyContent="space-between" zIndex={1}>
        <VStack
          gap={2}
          width="100%"
          paddingX={DEFAULT_MARGIN_MOBILE}
          paddingBottom={DEFAULT_MARGIN_MOBILE}
          flexGrow={1}
        >
          {header}
          {main}
          {hasSidebar && sidebar}
        </VStack>
        {footer && (
          <VStack width="100%" paddingX={DEFAULT_MARGIN_MOBILE} paddingBottom={4}>
            {footer}
          </VStack>
        )}
      </VStack>
    );
  }

  // Desktop with sidebar: two-column layout
  if (hasSidebar) {
    return (
      <VStack width="100%" alignItems="center" paddingX={DEFAULT_MARGIN} zIndex={1}>
        <VStack width="100%" maxWidth={TWO_COLUMN_MAX_WIDTH} gap={DEFAULT_MARGIN}>
          {header}
          <HStack gap={DEFAULT_CARD_SPACER} width="100%" alignItems="flex-start">
            <VStack flexGrow={1}>{main}</VStack>
            <VStack minWidth={DEFAULT_SIDEBAR_WIDTH} maxWidth={DEFAULT_SIDEBAR_WIDTH}>
              {sidebar}
            </VStack>
          </HStack>
        </VStack>
      </VStack>
    );
  }

  // Desktop without sidebar: centered single column
  return (
    <VStack width="100%" alignItems="center" paddingX={DEFAULT_MARGIN}>
      <VStack width="100%" maxWidth={SINGLE_COLUMN_MAX_WIDTH} gap={DEFAULT_CARD_SPACER}>
        {header}
        {main}
      </VStack>
    </VStack>
  );
});
